import { useCallback, useEffect } from "react";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { Tab } from "../../../../actions/Tenants/config/constantsTyped";
import { useUserPreferences } from "../../../../hooks";
import { useDateRangeQueryParams } from "../../../../hooks/useDateRangeQueryParams";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import { DATE_URL_FORMAT } from "./useRipeningRoom";


// * Re-usable hook to work with planning date range
export function useRipeningPlanningDateRange() {
    return useDateRangeQueryParams(0, 5);
}

export interface RipeningPlanningProps {
    getExtraFields: (activeTab: Tab) => string[];
    availableRipeningFields: any[];
    ripeningField: any;
    selectRipeningField: (field: string) => void;
    ripeningFieldName: string;
}

export function useRipeningPlanning(activeTab: Tab): RipeningPlanningProps {
    const { dateRange } = useRipeningPlanningDateRange();
    const config = useConfig();

    const [userPreferences, updatePreferences] = useUserPreferences();

    const getRipeningFieldsFromConfig = () => config.get_fruit_types().flatMap((f) => getFilledArrayOrDefault(f?.ripening_fields).filter((i) => isFilledArray(i.categories)).map((i, index) => ({
        ...i,
        label: `${f.text} - ${i.label}`,
        key: `${f.value}__${index}`,
    })));

    // * Shortcut to set the selected ripening field in the URL
    const selectRipeningField = (field: string) => {
        if (field) {
            const fruit_type = field.split("__")[0];
            updatePreferences({ ripening_field: field, fruit_types: [fruit_type] });
        } else {
            updatePreferences({ ripening_field: undefined, fruit_types: [] });
        }
    };

    const preferredRipeningField = userPreferences.ripening_field;


    // * For every fruit_type in filter, get the ripening fields
    const availableRipeningFields = getRipeningFieldsFromConfig();

    // * This effect is needed to make sure we filter for the correct fruit_types
    useEffect(() => {
        if (!activeTab?.show_class_distribution) {
            // * Only overwrite fruit_type filter if actually needed
            return;
        }
        if (isFilledArray(availableRipeningFields)) {
            if (preferredRipeningField) {
                const selectedRipeningField = availableRipeningFields.find((i) => i.key === preferredRipeningField);
                // * Reset the selected ripening field in the URL if fruit_type is not included in the available ripening fields
                if (!selectedRipeningField) {
                    selectRipeningField(availableRipeningFields[0].key);
                } else {
                    // * Make sure the fruit_type is selected in the filter
                    const fruitTypeOfPreferredRipeningField = preferredRipeningField.split("__")[0];
                    if (fruitTypeOfPreferredRipeningField !== userPreferences.fruit_types?.[0] || getFilledArrayOrDefault(userPreferences.fruit_types).length !== 1) {
                        selectRipeningField(availableRipeningFields[0].key);
                    }
                }
            }
            if (!preferredRipeningField) {
                selectRipeningField(availableRipeningFields[0].key);
            }
        }
    }, [availableRipeningFields.length, preferredRipeningField, activeTab?.show_class_distribution]);


    // * If no ripening field is selected in the URL, select the first available ripening field
    const ripeningField = availableRipeningFields.find((i) => i.key === preferredRipeningField) || availableRipeningFields?.[0];
    const ripeningFieldName = `dominant_${ripeningField?.category_name || "ripening_class"}`;

    // * collect extra fields to GET to populate the ripening class distribution
    const getExtraFields = useCallback((activeTab) => {
        if (!activeTab?.show_class_distribution) {
            return [];
        }
        const extraFields = dateRange.map((date) => {
            const formattedDate = date.format(DATE_URL_FORMAT);
            return `layer_meta.${ripeningFieldName}_${formattedDate}`;
        });
        return extraFields;
    }, [dateRange, ripeningFieldName]);


    return { getExtraFields, availableRipeningFields, ripeningField, selectRipeningField, ripeningFieldName };

}
